import { AddPublicationButton } from '../../../addPublicationButton/AddPublicationButton';
import { getDayViewCreateSlotState, isPastCalendarDay, type HourSlot } from '../functions';
import { VISIBLE_PUBS_PER_CELL } from '../calendarCellAnchor';
import { InlinePublicationCard } from '../InlinePublicationCard';
import { PublicationPill } from '../PublicationPill';
import { MoreCellPublicationsButton } from '../MoreCellPublicationsButton';
import { CalendarGranularity, CalendarPublication, CalendarPublicationHoverAnchor } from '../types';

const CELL_BASE_CLASS = 'group relative border-b border-r border-light-gray bg-white';
const PUBLICATIONS_WRAPPER_BASE_CLASS =
    'absolute inset-0.5 z-0 flex flex-col gap-0.5 overflow-hidden px-0.5 pb-0.5';
const PUBLICATIONS_WRAPPER_CLASS = `${PUBLICATIONS_WRAPPER_BASE_CLASS} pt-7`;
const PUBLICATIONS_WRAPPER_FILL_CELL_CLASS = PUBLICATIONS_WRAPPER_BASE_CLASS;
const ADD_BUTTON_OVERLAY_EMPTY_CLASS =
    'pointer-events-none absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100';
const ADD_BUTTON_OVERLAY_FILLED_CLASS =
    'pointer-events-none absolute right-0.5 top-0.5 z-10 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100';

type TimeSlotCellProps = Readonly<{
    day: Date;
    dayKey: string;
    slot: HourSlot;
    rowMinClass: string;
    isLastRow: boolean;
    isLastColumn: boolean;
    publications: readonly CalendarPublication[];
    inlineCardVariant: Extract<
        CalendarGranularity,
        CalendarGranularity.Week | CalendarGranularity.Day
    >;
    now: Date;
    onPublicationHoverStart: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    onPublicationHoverEnd: () => void;
    onOpenDayList: (day: Date, top: number, left: number) => void;
}>;

export function TimeSlotCell({
    day,
    dayKey,
    slot,
    rowMinClass,
    isLastRow,
    isLastColumn,
    publications,
    inlineCardVariant,
    now,
    onPublicationHoverStart,
    onPublicationHoverEnd,
    onOpenDayList,
}: TimeSlotCellProps) {
    const showInlineCard = publications.length === 1;
    const visible = publications.slice(0, VISIBLE_PUBS_PER_CELL);
    const extra = publications.length - VISIBLE_PUBS_PER_CELL;
    const isEmpty = publications.length === 0;
    const { showCreateButton, presetSchedule } =
        inlineCardVariant === CalendarGranularity.Day
            ? getDayViewCreateSlotState(day, dayKey, slot.hour, now)
            : {
                  showCreateButton: !isPastCalendarDay(day),
                  presetSchedule: {
                      date: dayKey,
                      hour: String(slot.hour).padStart(2, '0'),
                      minute: String(slot.minute).padStart(2, '0'),
                  },
              };

    const publicationsFillCell = !showCreateButton && showInlineCard;
    const publicationsWrapperClass = publicationsFillCell
        ? PUBLICATIONS_WRAPPER_FILL_CELL_CLASS
        : showCreateButton
          ? PUBLICATIONS_WRAPPER_CLASS
          : PUBLICATIONS_WRAPPER_BASE_CLASS;

    const cellClass = [
        CELL_BASE_CLASS,
        rowMinClass,
        isLastRow ? 'border-b-0' : '',
        isLastColumn ? 'border-r-0' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div data-calendar-day-cell className={cellClass}>
            {!isEmpty && (
                <div className={publicationsWrapperClass}>
                    {showInlineCard ? (
                        <InlinePublicationCard
                            publication={publications[0]}
                            index={0}
                            variant={inlineCardVariant}
                        />
                    ) : (
                        visible.map((publication, index) => (
                            <PublicationPill
                                key={publication.id}
                                publication={publication}
                                index={index}
                                onHoverStart={onPublicationHoverStart}
                                onHoverEnd={onPublicationHoverEnd}
                            />
                        ))
                    )}
                    {!showInlineCard && extra > 0 && (
                        <MoreCellPublicationsButton
                            day={day}
                            extra={extra}
                            onOpenDayList={onOpenDayList}
                        />
                    )}
                </div>
            )}
            {showCreateButton ? (
                <div
                    className={
                        isEmpty ? ADD_BUTTON_OVERLAY_EMPTY_CLASS : ADD_BUTTON_OVERLAY_FILLED_CLASS
                    }
                >
                    <AddPublicationButton
                        compact
                        excludeFromTabOrder
                        presetSchedule={presetSchedule}
                    />
                </div>
            ) : null}
        </div>
    );
}
